// Draws a box level (built-in maps and levels from the level builder): procedurally textured boxes
// merged by material, bhop blocks as their own meshes so they can drop and glow, zone frames, a
// gradient sky and a far-away floor.

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { ZONE_MARGIN, type Blinker, type BoxLevel, type Mover, type Spinner } from '../maps/boxlevel/build';
import type { LevelBox, LevelProp } from '../maps/boxlevel/types';
import { sceneryModel } from '../assets/kenney';
import type { BhopBlock } from '../sim/bhopBlocks';
import { BLOCK_TICKS } from '../sim/constants';
import type { Simulation } from '../sim/simulation';
import type { Zone } from '../sim/zones';
import { makeSourceSpace, sizeToThree, sourceToThree } from './coords';
import { shapeGeometry } from './shapeGeometry';
import type { LevelView } from './levelView';
import { disposeTree } from './levelView';
import { disposeLevelMaterials, isSharedLevelMaterial, levelMaterial } from './proceduralTextures';

interface BlockView {
  block: BhopBlock;
  mesh: THREE.Mesh;
  /** The look's shared material, and its glowing ones (the block you're on, a dropping block). */
  shared: THREE.Material;
  glow: Glow;
  baseY: number;
}

/**
 * Glowing copies of a block look: one for the block you're standing on (its red grows as its time
 * runs out; only one at a time) and one for a dropping block. Blocks of a look share its material
 * otherwise: one material for many blocks is cheap to draw (switching materials refreshes uniforms).
 */
interface Glow {
  contact: THREE.MeshPhongMaterial;
  down: THREE.MeshPhongMaterial;
  base: THREE.Color;
}

const size = new THREE.Vector3();
const centre = new THREE.Vector3();
const RED = new THREE.Color(0xff3020);

/**
 * What a scenery material looks like: models of one kit share materials of the same name (and the
 * kit's one texture sheet); Kenney's nature models are told apart by colour too.
 */
function lookKey(model: string, m: THREE.Material): string {
  const s = m as THREE.MeshStandardMaterial;
  const kit = model.includes('-') ? model.slice(0, model.indexOf('-')) : 'nature';
  return [
    kit,
    m.name,
    m.type,
    s.color?.getHexString(),
    s.map ? 1 : 0,
    m.transparent,
    m.alphaTest,
    m.side,
  ].join('|');
}

/** Box geometry in three.js space with one texture tile per 128 units on every face. */
export function boxGeometry(
  min: readonly number[],
  max: readonly number[],
  atCentre: boolean,
): THREE.BoxGeometry {
  sizeToThree(size, max[0] - min[0], max[1] - min[1], max[2] - min[2]);
  const geo = new THREE.BoxGeometry(size.x, size.y, size.z);
  const uv = geo.attributes.uv as THREE.BufferAttribute;
  const dims = [
    [size.z, size.y],
    [size.z, size.y],
    [size.x, size.z],
    [size.x, size.z],
    [size.x, size.y],
    [size.x, size.y],
  ];
  for (let f = 0; f < 6; f++)
    for (let v = 0; v < 4; v++) {
      const i = f * 4 + v;
      uv.setXY(i, (uv.getX(i) * dims[f][0]) / 128, (uv.getY(i) * dims[f][1]) / 128);
    }
  if (!atCentre) {
    sourceToThree(centre, (min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2);
    geo.translate(centre.x, centre.y, centre.z);
  }
  return geo;
}

/** A scenery model placed in Source space: glTF is Y-up, Source is Z-up. */
export function propObject(model: THREE.Object3D, p: LevelProp): THREE.Object3D {
  const outer = new THREE.Group();
  outer.position.set(p.pos[0], p.pos[1], p.pos[2]);
  outer.rotation.z = p.yaw;
  outer.scale.setScalar(p.scale);
  const inner = model.clone();
  inner.rotation.x = Math.PI / 2;
  outer.add(inner);
  return outer;
}

function skyDome(top: string, horizon: string): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: { top: { value: new THREE.Color(top) }, horizon: { value: new THREE.Color(horizon) } },
    vertexShader: `varying vec3 vDir; void main() { vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `uniform vec3 top; uniform vec3 horizon; varying vec3 vDir;
      void main() { float h = clamp(vDir.y, -0.2, 1.0); gl_FragColor = vec4(mix(horizon, top, pow(max(h, 0.0), 0.6)), 1.0); }`,
  });
  const m = new THREE.Mesh(new THREE.SphereGeometry(20000, 32, 16), mat);
  m.renderOrder = -1;
  m.frustumCulled = false;
  return m;
}

export class BoxLevelView implements LevelView {
  readonly root = new THREE.Group();
  readonly fog: [number, number];
  readonly fogColor: string;
  /** Fog hides everything past ~16000; a nearer far plane keeps depth precise (no flicker). */
  readonly far = 25000;
  private readonly blockViews: BlockView[] = [];
  private readonly moverViews: { mover: Mover; mesh: THREE.Mesh }[] = [];
  private readonly spinViews: { spinner: Spinner; mesh: THREE.Mesh }[] = [];
  private readonly blinkViews: { blinker: Blinker; mesh: THREE.Mesh }[] = [];
  private readonly sky: THREE.Mesh;

  /**
   * `mergeScenery`: draw the scenery models merged (one mesh per material and map chunk) instead of
   * one object each: hundreds of models in a few draw calls. Off in the builder, which rebuilds the
   * view after every edit.
   */
  constructor(
    level: BoxLevel,
    anisotropy: number,
    private readonly mergeScenery = true,
  ) {
    const theme = level.def.theme;
    this.fog = [theme.fogNear, theme.fogFar];
    this.fogColor = theme.fog;
    this.sky = skyDome(theme.skyTop, theme.skyHorizon);
    this.root.add(this.sky);

    // Pieces live in a Source-space group (Source units and axes).
    const src = new THREE.Group();
    makeSourceSpace(src);
    this.root.add(src);
    // Static pieces merged per material; blocks and moving pieces get their own mesh.
    const groups = new Map<string, { def: LevelBox; geos: THREE.BufferGeometry[] }>();
    let minZ = Infinity;
    const centreOf = (d: LevelBox) => [
      (d.min[0] + d.max[0]) / 2,
      (d.min[1] + d.max[1]) / 2,
      (d.min[2] + d.max[2]) / 2,
    ];
    const sizeOf = (d: LevelBox) => [d.max[0] - d.min[0], d.max[1] - d.min[1], d.max[2] - d.min[2]];
    for (const b of level.boxes) {
      if (!b.block && !b.mover && !b.spinner && !b.blinker) continue;
      const d = b.def;
      const shared = levelMaterial(d.material, d.color, anisotropy);
      const c = centreOf(d);
      const mesh = new THREE.Mesh(shapeGeometry(d.shape ?? 'box', sizeOf(d), c, d.turn ?? 0, false), shared);
      mesh.position.set(c[0], c[1], c[2]);
      src.add(mesh);
      if (b.block)
        this.blockViews.push({ block: b.block, mesh, shared, glow: this.glowOf(shared, src), baseY: c[2] });
      if (b.mover) this.moverViews.push({ mover: b.mover, mesh });
      if (b.spinner) this.spinViews.push({ spinner: b.spinner, mesh });
      if (b.blinker) this.blinkViews.push({ blinker: b.blinker, mesh });
    }
    // Everything else, scenery included, from the level definition.
    const dynamic = new Set(
      level.boxes.filter((b) => b.block || b.mover || b.spinner || b.blinker).map((b) => b.def),
    );
    for (const d of level.def.boxes) {
      minZ = Math.min(minZ, d.min[2]);
      if (dynamic.has(d)) continue;
      const key = d.material + d.color;
      let g = groups.get(key);
      if (!g) groups.set(key, (g = { def: d, geos: [] }));
      g.geos.push(shapeGeometry(d.shape ?? 'box', sizeOf(d), centreOf(d), d.turn ?? 0, true));
    }
    for (const g of groups.values()) {
      const merged = mergeGeometries(g.geos);
      for (const geo of g.geos) geo.dispose();
      if (!merged) continue;
      const mesh = new THREE.Mesh(merged, levelMaterial(g.def.material, g.def.color, anisotropy));
      mesh.matrixAutoUpdate = false;
      src.add(mesh);
    }

    if (level.def.props?.length) void this.loadProps(level.def.props, src);

    // The start zone is the platform itself; stage and end zones reach ZONE_MARGIN past theirs.
    this.zone(level.startZone, 0x4fd27a, 0);
    for (const z of level.checkpointZones) this.zone(z, 0x5ad1ff, ZONE_MARGIN);
    this.zone(level.endZone, 0xff5a5a, ZONE_MARGIN);

    // A floor far below so the void isn't empty.
    const floor = new THREE.Mesh(
      new THREE.PlaneGeometry(200000, 200000),
      new THREE.MeshLambertMaterial({ color: new THREE.Color(theme.voidColor) }),
    );
    floor.rotation.x = -Math.PI / 2;
    sourceToThree(floor.position, 0, 0, Math.min(level.def.failZ, minZ) - 400);
    this.root.add(floor);
  }

  private readonly glows = new Map<THREE.Material, Glow>();

  /** A block look's glowing materials, made once per look (on hidden meshes, so they're warmed up too). */
  private glowOf(shared: THREE.Material, src: THREE.Object3D): Glow {
    let g = this.glows.get(shared);
    if (!g) {
      const base = (shared as THREE.MeshPhongMaterial).color.clone();
      const contact = shared.clone() as THREE.MeshPhongMaterial;
      const down = shared.clone() as THREE.MeshPhongMaterial;
      down.color.copy(base).lerp(RED, 0.75);
      g = { contact, down, base };
      this.glows.set(shared, g);
      for (const m of [contact, down]) {
        const holder = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), m);
        holder.visible = false;
        src.add(holder);
      }
    }
    return g;
  }

  /** Called when scenery models finish loading (the renderer warms them up). */
  onAssetsLoaded: (() => void) | null = null;

  private async loadProps(props: LevelProp[], src: THREE.Object3D) {
    const models = await Promise.all(props.map((p) => sceneryModel(p.model)));
    if (!this.mergeScenery) {
      props.forEach((p, i) => models[i] && src.add(propObject(models[i], p)));
      this.onAssetsLoaded?.();
      return;
    }
    // Static scenery: every model's meshes baked into one mesh per material and 4096-unit chunk of
    // the map (few draw calls, and chunks out of view are still culled).
    const groups = new Map<string, { mat: THREE.Material; geos: THREE.BufferGeometry[] }>();
    props.forEach((p, i) => {
      const model = models[i];
      if (!model) return;
      const o = propObject(model, p);
      o.updateMatrixWorld(true);
      const cell = Math.floor(p.pos[0] / 4096) + ',' + Math.floor(p.pos[1] / 4096);
      o.traverse((c) => {
        const mesh = c as THREE.Mesh;
        if (!mesh.isMesh || Array.isArray(mesh.material)) return;
        let g = mesh.geometry.clone().applyMatrix4(mesh.matrixWorld);
        if (g.index) g = g.toNonIndexed();
        // By what the material looks like, not which copy it is: every model file loads its own
        // copies of its kit's materials (the same colours, the same texture sheet).
        const key =
          lookKey(p.model, mesh.material) + '|' + Object.keys(g.attributes).sort().join(',') + '|' + cell;
        let e = groups.get(key);
        if (!e) groups.set(key, (e = { mat: mesh.material, geos: [] }));
        e.geos.push(g);
      });
    });
    for (const { mat, geos } of groups.values()) {
      const merged = mergeGeometries(geos);
      for (const g of geos) g.dispose();
      if (!merged) continue;
      const mesh = new THREE.Mesh(merged, mat);
      mesh.matrixAutoUpdate = false;
      src.add(mesh);
    }
    this.onAssetsLoaded?.();
  }

  /** Glowing frame showing a zone on its platform (the zone itself reaches much higher). */
  /** Outline of a zone, drawn on its platform's edges (the zone itself reaches `margin` further). */
  private zone(z: Zone, color: number, margin: number) {
    const h = 96;
    const m = margin;
    sizeToThree(size, z.max[0] - z.min[0] - 2 * m, z.max[1] - z.min[1] - 2 * m, h);
    const geo = new THREE.BoxGeometry(size.x, size.y, size.z);
    const fill = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.08,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    const edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(geo),
      new THREE.LineBasicMaterial({ color }),
    );
    // Zones start 64 below the platform top; draw the frame sitting on the platform.
    // Lifted 2 units so the frame never shares a plane with the platform top (z-fighting).
    sourceToThree(centre, (z.min[0] + z.max[0]) / 2, (z.min[1] + z.max[1]) / 2, z.min[2] + 64 + 2 + h / 2);
    fill.position.copy(centre);
    edges.position.copy(centre);
    this.root.add(fill, edges);
  }

  update(sim: Simulation, alpha: number) {
    const p = sim.player.pos;
    sourceToThree(this.sky.position, p[0], p[1], p[2]);
    for (const v of this.moverViews) {
      const m = v.mover;
      v.mesh.position.set(
        m.prev[0] + (m.pos[0] - m.prev[0]) * alpha,
        m.prev[1] + (m.pos[1] - m.prev[1]) * alpha,
        m.prev[2] + (m.pos[2] - m.prev[2]) * alpha,
      );
    }
    for (const v of this.spinViews) {
      const sp = v.spinner;
      v.mesh.rotation.z = sp.prevYaw + (sp.yaw - sp.prevYaw) * alpha;
    }
    for (const v of this.blinkViews) v.mesh.visible = v.blinker.on;
    const contactBlock = sim.contact.block;
    const contactTicks = sim.contact.ticks;
    for (const v of this.blockViews) {
      const b = v.block;
      v.mesh.position.z = v.baseY + b.offset;
      v.mesh.visible = b.state !== 'down';
      let mat = v.shared;
      if (b.state === 'falling' || b.state === 'down') mat = v.glow.down;
      else if (b === contactBlock && contactTicks > 0) {
        mat = v.glow.contact;
        v.glow.contact.color.copy(v.glow.base).lerp(RED, Math.min(1, contactTicks / BLOCK_TICKS) * 0.75);
      }
      if (v.mesh.material !== mat) v.mesh.material = mat;
    }
  }

  /**
   * Keep the shared (cached) materials when this view goes away: the level builder rebuilds the
   * view after every edit and redrawing every texture each time would stall.
   */
  keepMaterials = false;

  dispose() {
    if (!this.keepMaterials) {
      disposeTree(this.root);
      disposeLevelMaterials();
      return;
    }
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      m.geometry?.dispose();
      const mat = m.material;
      // Per-block copies share the cached textures: dispose the copy, not its maps.
      if (mat) for (const x of Array.isArray(mat) ? mat : [mat]) if (!isSharedLevelMaterial(x)) x.dispose();
    });
    this.root.removeFromParent();
  }
}
